import {Injectable} from '@angular/core';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {CustomJsonDataService, DcpBundleTemplate} from '@dcp/configurator';
import {UntilDestroy} from '@ngneat/until-destroy';
import {OrderEntry} from '@spartacus/cart/base/root';
import {OrderEntryConfiguration} from '../model/order/dcp-order-entry.model';

@Injectable({
  providedIn: 'root',
})
@UntilDestroy()
export class DcpOrderEntryService {
  constructor(
    protected jsonDataService: CustomJsonDataService,
    private productUtilitiesService: DcpProductUtilitiesService
  ) {}

  /**
   * Following public functions help extracting customJsonData from the OrderEntry.data array
   * Each function retrieves data for one of the corresponding OrderEntry.data entries
   *
   * @param orderEntry - OrderEntry where customJsonData should be extracted from
   */
  public getConfiguration(orderEntry: OrderEntry): OrderEntryConfiguration {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'configuration');
  }

  public getSalesItemAspects(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'salesItemAspects');
  }

  public getSalesArea(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'salesArea');
  }

  public getReferencedDocuments(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'referencedDocuments');
  }

  public getFulfillmentAspects(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'fulfillmentAspects');
  }

  public getServiceLevelAgreements(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'serviceLevelAgreements');
  }

  public getAddresses(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'addresses');
  }

  public getTermsAndConditionsParams(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'termsAndConditionsParams');
  }

  public getPriceDetails(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'priceDetails');
  }

  public getPriceComponents(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'priceComponents');
  }

  public getPriceCalculationBreakdown(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'priceCalculationBreakdown');
  }

  public getSubtotals(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'subtotals');
  }

  public getPlaceOrderWarnings(orderEntry: OrderEntry): any {
    return this.jsonDataService.getDataFieldOfType(orderEntry, 'place_order_warnings');
  }

  /**
   * Returns the subtotal for a given product inside a split order-entry.
   */
  public getSubtotalByCode(orderEntry: OrderEntry, code: string): string {
    return this.getSubtotalFromJson(orderEntry, code);
  }

  /**
   * Returns the subtotal for a given order entry. In case of cps configured products this is part of the json data,
   * for legacy configured products this is taken from the order entry directly.
   *
   * @param orderEntry OrderEntry to get the subtotal for
   * @return string containing the formatted subtotal price
   */
  public getSubtotal(orderEntry: OrderEntry): string {
    const subtotals = this.getSubtotals(orderEntry);
    return (
      subtotals?.find((subtotal) => subtotal?.code?.endsWith('_all'))?.formattedValues?.totalValue ||
      orderEntry?.totalPrice?.formattedValue
    );
  }

  /**
   *
   * @param orderEntry OrderEntry to get the discounts for
   * @param code the product code
   */
  public getDiscountFromSubtotal(orderEntry: OrderEntry, code: string): string {
    const subtotals = this.getSubtotals(orderEntry);
    const editionsAddonsSubtotal = subtotals?.find((subtotal) => subtotal?.code === `${code}_editions_addons`);
    return editionsAddonsSubtotal?.absoluteTotalValue > 0
      ? editionsAddonsSubtotal.formattedValues?.absoluteTotalValue
      : null;
  }

  public getPriceCalculationBreakdownFromSubtotals(orderEntry: OrderEntry, code: string): any {
    const subtotals = this.getSubtotals(orderEntry);
    const priceCalculationBreakdowns = subtotals?.find(
      (subtotal) => subtotal?.code === `${code}_editions_addons`
    )?.priceCalculationBreakdown;
    return priceCalculationBreakdowns;
  }

  public populatePriceTooltipDataItem(orderEntry: OrderEntry): any {
    const priceDetails = this.getPriceDetails(orderEntry);
    return {
      materialName: orderEntry?.product?.name,
      netPriceDescription: priceDetails?.formattedValues?.netPriceDescription,
      totalValue: priceDetails?.formattedValues?.totalValue,
    };
  }

  /**
   * Returns the bundle template for a given order entry.
   * @param orderEntry
   */
  public getBundleTemplate(orderEntry: OrderEntry): DcpBundleTemplate {
    const materialId = this.getConfiguration(orderEntry)?.product;
    return this.productUtilitiesService.fetchBundleTemplateByCanonicalId(orderEntry?.product, materialId);
  }

  private getSubtotalFromJson(orderEntry: OrderEntry, code: string): string {
    const subtotals = this.getSubtotals(orderEntry);
    const addonsSubtotal = subtotals?.find((subtotal) => subtotal?.code === `${code}_editions_addons`)?.formattedValues
      ?.totalValue;
    return (
      addonsSubtotal ||
      subtotals?.find((subtotal) => subtotal?.code?.endsWith(`${code}_all`))?.formattedValues?.totalValue ||
      orderEntry?.totalPrice?.formattedValue
    );
  }
}
